import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Instagram, Youtube, Linkedin, Twitter, BookText as TikTok, Plus } from 'lucide-react';
import type { Post } from '../../types/Post';
import PostModal from '../posts/PostModal';
import NewPostModal from '../posts/NewPostModal';

type CalendarViewProps = {
  posts: Post[];
  onAddPost?: (post: Omit<Post, 'id' | 'profile_id' | 'created_at' | 'updated_at'>) => void;
};

type CalendarItem = {
  date: Date;
  posts: Post[];
};

export default function CalendarView({ posts, onAddPost }: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: CalendarItem[] = [];

    const startPadding = firstDay.getDay();
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, posts: [] });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const dayPosts = posts.filter(post => {
        if (!post.scheduled_date) return false;
        const postDate = new Date(post.scheduled_date);
        return (
          postDate.getDate() === date.getDate() &&
          postDate.getMonth() === date.getMonth() &&
          postDate.getFullYear() === date.getFullYear()
        );
      });
      days.push({ date, posts: dayPosts });
    }

    const endPadding = 42 - days.length;
    for (let i = 1; i <= endPadding; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, posts: [] });
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const getChannelIcon = (channel: string) => {
    const icons = {
      instagram: <Instagram className="w-3 h-3" />,
      youtube: <Youtube className="w-3 h-3" />,
      linkedin: <Linkedin className="w-3 h-3" />,
      twitter: <Twitter className="w-3 h-3" />,
      tiktok: <TikTok className="w-3 h-3" />
    };
    return icons[channel as keyof typeof icons];
  };

  const getChannelColor = (channel: string) => {
    const colors = {
      instagram: 'bg-pink-50 text-pink-800',
      youtube: 'bg-red-50 text-red-800',
      linkedin: 'bg-blue-50 text-blue-800',
      twitter: 'bg-sky-50 text-sky-800',
      tiktok: 'bg-purple-50 text-purple-800'
    };
    return colors[channel as keyof typeof colors];
  };

  const handleNewPost = (date: Date) => {
    setSelectedDate(date);
    setShowNewPostModal(true);
  };

  const handleSaveNewPost = (post: Omit<Post, 'id' | 'profile_id' | 'created_at' | 'updated_at'>) => {
    if (onAddPost) {
      onAddPost({
        ...post,
        scheduled_date: selectedDate?.toISOString() || post.scheduled_date
      });
    }
    setShowNewPostModal(false);
    setSelectedDate(null);
  };

  return (
    <div className="bg-white border">
      {/* Header */}
      <div className="p-3 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h2 className="text-lg font-semibold">{formatDate(currentDate)}</h2>
            <button
              onClick={() => navigateMonth('next')}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div>
        {/* Calendar Grid */}
        <div className="grid grid-cols-7 border-b">
          {/* Weekday Headers */}
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day, index) => (
            <div 
              key={day} 
              className={`text-center font-medium text-gray-500 text-sm py-2 ${
                index < 6 ? 'border-r' : ''
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {/* Calendar Days */}
          {getCalendarDays().map((item, index) => {
            const isCurrentMonth = item.date.getMonth() === currentDate.getMonth();
            const isToday = item.date.toDateString() === new Date().toDateString();
            const isLastInRow = (index + 1) % 7 === 0;
            const isLastRow = index >= 35;

            return (
              <div
                key={index}
                className={`min-h-[120px] p-2 group relative ${
                  !isLastInRow ? 'border-r' : ''
                } ${
                  !isLastRow ? 'border-b' : ''
                }`}
              >
                <div className="text-right mb-2">
                  <span
                    className={`text-sm ${
                      isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                    } ${isToday ? 'font-bold' : ''}`}
                  >
                    {item.date.getDate()}
                  </span>
                </div>

                {/* New Post Button - Shows on Hover */}
                <button
                  onClick={() => handleNewPost(item.date)}
                  className="absolute top-1 left-1 px-2 py-1 bg-blue-500 text-white rounded text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-600"
                >
                  <Plus className="w-3 h-3" />
                  <span>Novo Post</span>
                </button>

                <div className="space-y-1">
                  {item.posts.map(post => (
                    <button
                      key={post.id}
                      onClick={() => setSelectedPost(post)}
                      className={`w-full text-left text-xs p-1 hover:bg-gray-50 transition-colors ${getChannelColor(post.channel)}`}
                      title={post.title}
                    >
                      <div className="flex items-center gap-1">
                        {getChannelIcon(post.channel)}
                        <span className="truncate text-[11px] leading-tight">{post.title}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Post Modal */}
      {selectedPost && (
        <PostModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onSave={(updates) => {
            // Handle post updates here
            setSelectedPost(null);
          }}
          onDelete={() => {
            // Handle post deletion here
            setSelectedPost(null);
          }}
        />
      )}

      {/* New Post Modal */}
      {showNewPostModal && (
        <NewPostModal
          isOpen={showNewPostModal}
          onClose={() => {
            setShowNewPostModal(false);
            setSelectedDate(null);
          }}
          onSave={handleSaveNewPost}
          initialDate={selectedDate}
        />
      )}
    </div>
  );
}